import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Star, Search, Loader2, ChevronLeft, ChevronRight,
  MessageSquare, ArrowRight, X, Check
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const AVATAR_COLORS = ['#572B7C', '#0F766E', '#2563EB', '#78287C', '#15803D', '#D97706'];
const avBg = (i) => AVATAR_COLORS[((i % AVATAR_COLORS.length) + AVATAR_COLORS.length) % AVATAR_COLORS.length];
const initials = (n) => {
  const p = (n || '').trim().split(/\s+/);
  return (((p[0] || '')[0] || '') + ((p[1] || '')[0] || '')).toUpperCase() || '?';
};

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [ratingFilter, setRatingFilter] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('shootzu_admin_token');
      const params = { page, limit: 20 };
      if (searchQuery) params.search = searchQuery;
      if (ratingFilter) params.rating = ratingFilter;
      const res = await axios.get(`${BACKEND_URL}/api/admin/reviews`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setReviews(res.data.reviews || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, ratingFilter]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchReviews();
  };

  const openEdit = (review) => {
    setEditingReview(review);
    setEditRating(review.rating || 0);
    setEditText(review.review_text || review.comment || '');
  };

  const handleSave = async () => {
    if (!editingReview) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('shootzu_admin_token');
      await axios.put(`${BACKEND_URL}/api/admin/reviews/${editingReview.id}`, {
        rating: editRating,
        review_text: editText,
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      toast.success('Review updated');
      setEditingReview(null);
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to remove this review?')) return;
    setDeletingId(id);
    try {
      const token = localStorage.getItem('shootzu_admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/reviews/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Review removed');
      fetchReviews();
    } catch (err) {
      toast.error('Failed to remove');
    } finally {
      setDeletingId(null);
    }
  };

  const StarRow = ({ rating, size = 16 }) => (
    <div className="flex" style={{ gap: 2 }}>
      {[0, 1, 2, 3, 4].map(k => (
        <Star key={k} style={{ width: size, height: size }} strokeWidth={1.5}
          fill={k < rating ? '#F59E0B' : '#E6E9EF'}
          color={k < rating ? '#F59E0B' : '#E6E9EF'} />
      ))}
    </div>
  );

  const ratingFilters = [
    { label: 'All', value: null },
    { label: '5★', value: 5 },
    { label: '4★', value: 4 },
    { label: '3★', value: 3 },
    { label: '2★', value: 2 },
    { label: '1★', value: 1 },
  ];

  return (
    <>
      {/* Sticky header */}
      <header className="sz-header">
        <h1 className="sz-h1" style={{ margin: 0 }}>Reviews</h1>
        <div style={{ fontSize: 12.5, color: '#8A90A2', marginTop: 3 }}>
          {total} {total === 1 ? 'review' : 'reviews'}
        </div>
      </header>

      {/* Scroll body */}
      <div style={{ padding: '22px 30px 56px', maxWidth: 980 }}>
        {/* Toolbar: search + rating pills */}
        <div className="sz-card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <form onSubmit={handleSearch} style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16 }} color="#A8AEBC" />
            <input
              className="sz-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search reviewer, reviewee or job…"
              style={{ paddingLeft: 36 }}
            />
          </form>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ratingFilters.map(f => (
              <button
                key={f.label}
                type="button"
                className={`sz-pill${ratingFilter === f.value ? ' sz-active' : ''}`}
                onClick={() => { setRatingFilter(f.value); setPage(1); }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Reviews list */}
        {loading ? (
          <div className="flex items-center justify-center" style={{ padding: '80px 0' }}>
            <Loader2 className="animate-spin" style={{ width: 30, height: 30 }} color="#572B7C" />
          </div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#8A90A2', fontSize: 13 }}>
            <MessageSquare style={{ width: 40, height: 40, margin: '0 auto 12px', display: 'block' }} color="#CBD0DA" />
            No reviews match your filters.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
            {reviews.map((review, i) => {
              const reviewerType = review.reviewer_type === 'client' ? 'Job Owner' : 'Crew';
              const typeColors = reviewerType === 'Job Owner'
                ? { fg: '#2563EB', bg: '#DBEAFE' }
                : { fg: '#0F766E', bg: '#CCFBF1' };
              const reviewerName = review.reviewer_name || 'Unknown';
              const revieweeName = review.reviewee_name || 'Unknown';
              const text = review.review_text || review.comment || 'No written review';
              return (
                <div key={review.id} className="sz-card sz-fade" style={{ padding: '18px 20px' }}>
                  {/* Reviewer → Reviewee + stars */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    {/* Reviewer */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {review.reviewer_photo ? (
                        <img src={review.reviewer_photo} alt="" style={{ width: 38, height: 38, borderRadius: 999, objectFit: 'cover', flex: '0 0 auto' }} />
                      ) : (
                        <div style={{ width: 38, height: 38, borderRadius: 999, background: avBg(i), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Poppins', fontWeight: 700, fontSize: 12, flex: '0 0 auto' }}>
                          {initials(reviewerName)}
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{reviewerName}</div>
                        <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'Poppins', color: typeColors.fg, background: typeColors.bg, padding: '2px 8px', borderRadius: 999 }}>{reviewerType}</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ArrowRight style={{ width: 20, height: 20, flex: '0 0 auto' }} color="#CBD0DA" strokeWidth={2} />

                    {/* Reviewee */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 140 }}>
                      {review.reviewee_photo ? (
                        <img src={review.reviewee_photo} alt="" style={{ width: 38, height: 38, borderRadius: 999, objectFit: 'cover', flex: '0 0 auto' }} />
                      ) : (
                        <div style={{ width: 38, height: 38, borderRadius: 999, background: avBg(i + 3), color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Poppins', fontWeight: 700, fontSize: 12, flex: '0 0 auto' }}>
                          {initials(revieweeName)}
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{revieweeName}</div>
                        <div style={{ fontSize: 11.5, color: '#8A90A2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{review.reviewee_email}</div>
                      </div>
                    </div>

                    {/* Stars */}
                    <StarRow rating={review.rating} size={16} />
                  </div>

                  {/* Quote */}
                  <p style={{ fontSize: 13, lineHeight: 1.55, color: '#2B3142', margin: '14px 0 0' }}>“{text}”</p>

                  {/* Meta + actions */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 13, flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ fontSize: 11.5, color: '#8A90A2', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {(review.job_title || 'N/A')} · {formatDate(review.created_at)}
                      {review.edited_by_admin && (
                        <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'Poppins', color: '#B45309', background: '#FEF3C7', padding: '2px 8px', borderRadius: 999 }}>Edited by Admin</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 7 }}>
                      <button type="button" onClick={() => openEdit(review)}
                        style={{ padding: '7px 14px', border: '1px solid #E0E3EB', borderRadius: 9, background: '#fff', fontSize: 12, fontWeight: 600, color: '#2B3142', cursor: 'pointer' }}>
                        Edit
                      </button>
                      <button type="button" onClick={() => handleDelete(review.id)} disabled={deletingId === review.id}
                        style={{ padding: '7px 14px', border: '1px solid #FECACA', borderRadius: 9, background: '#fff', fontSize: 12, fontWeight: 600, color: '#DC2626', cursor: deletingId === review.id ? 'not-allowed' : 'pointer', opacity: deletingId === review.id ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {deletingId === review.id ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> : null}
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 24 }}>
            <button type="button" className="sz-btn sz-btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '8px 12px' }}>
              <ChevronLeft style={{ width: 16, height: 16 }} />
            </button>
            <span style={{ fontSize: 12.5, color: '#8A90A2' }}>Page {page} of {pages}</span>
            <button type="button" className="sz-btn sz-btn-outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)} style={{ padding: '8px 12px' }}>
              <ChevronRight style={{ width: 16, height: 16 }} />
            </button>
          </div>
        )}
      </div>

      {/* Edit Review modal */}
      {editingReview && (
        <div className="sz-modal-backdrop" onClick={() => setEditingReview(null)}>
          <div className="sz-modal-card" style={{ width: '100%', maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="sz-modal-header-grad" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px' }}>
              <h3 className="sz-h3" style={{ margin: 0, color: '#fff' }}>Edit Review</h3>
              <button type="button" onClick={() => setEditingReview(null)}
                style={{ width: 30, height: 30, border: 'none', borderRadius: 9, background: 'rgba(255,255,255,.18)', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>
            <div style={{ padding: '20px 22px' }}>
              <div style={{ fontSize: 12.5, color: '#8A90A2', marginBottom: 14 }}>
                {editingReview.reviewer_name} → {editingReview.reviewee_name}
              </div>

              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#2B3142', marginBottom: 8 }}>Rating</label>
              <div style={{ display: 'flex', gap: 5, marginBottom: 16 }}>
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} type="button" onClick={() => setEditRating(s)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex' }}>
                    <Star style={{ width: 28, height: 28 }} strokeWidth={1.5}
                      fill={s <= editRating ? '#F59E0B' : '#E6E9EF'}
                      color={s <= editRating ? '#F59E0B' : '#E6E9EF'} />
                  </button>
                ))}
              </div>

              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#2B3142', marginBottom: 8 }}>Review text</label>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                className="sz-input"
                placeholder="Review text…"
                style={{ height: 100, resize: 'none', padding: '11px 13px', fontFamily: 'Work Sans' }}
              />

              <div style={{ display: 'flex', gap: 9, marginTop: 18 }}>
                <button type="button" onClick={() => setEditingReview(null)}
                  style={{ flex: 1, padding: 11, border: '1px solid #E0E3EB', borderRadius: 11, background: '#fff', fontSize: 13, fontWeight: 600, color: '#2B3142', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="button" onClick={handleSave} disabled={saving || editRating === 0}
                  style={{ flex: 1.4, padding: 11, border: 'none', borderRadius: 11, background: '#572B7C', color: '#fff', fontSize: 13, fontWeight: 600, cursor: (saving || editRating === 0) ? 'not-allowed' : 'pointer', opacity: (saving || editRating === 0) ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                  {saving ? <><Loader2 className="animate-spin" style={{ width: 15, height: 15 }} /> Saving…</> : <><Check style={{ width: 15, height: 15 }} /> Save Changes</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminReviews;
